import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FlatListProps } from 'react-native';
import { GoodsCard, List, useScreenSize } from '@banhala/ui-kit';
import { isGoodsCard, type CardEntity } from '../TwoColCardList/types';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { AblyGoodsCardActionType } from '../../../../api/types';
import { bridge, isWeb, useNavigator } from '../../../../utils';
import { useAddLikeGoods, type useLikeMap } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { useLogOnGoodsPressItem } from '../ThreeColGoodsList/log/logOnGoodsPressItem';
import { useLogOnPressLike } from '../ThreeColGoodsList/log/logOnPressLike';
import { useRemoveLikeGoods } from '../../../../queries';
import {
  cardRenderItemGenerator,
  type CardRenderItem,
} from '../TwoColCardList/cardRenderItemGenerator';
import { PrimaryCard } from '../TwoColCardList/PrimaryCard';
import { VideoGoodsCard, FullVideoGoodsCard } from '../TwoColCardList/VideoGoodsCard';
import { useLogDisplayCardItems } from './log/useLogDisplayCardItems';
import type { OnReturnAction } from '../../types';

const NUM_COLUMNS = 3;
const COLUMN_GAP = 2;

interface ThreeColCardListProps extends ReturnType<typeof useLikeMap> {
  entity: CardEntity[];
  logDisabled?: boolean;
  onReturnAction?: OnReturnAction;
  resetInjected?: VoidFunction;
  componentIndex?: number;
}

export const ThreeColCardList = ({
  entity,
  likeMap,
  setLikeMap,
  logDisabled,
  onPressMore,
  onReturnAction,
  resetInjected,
  componentIndex,
}: ThreeColCardListProps & {
  onPressMore?: (goodsSno: number, feedbackType: string) => void;
}) => {
  const { navigator } = useNavigator();
  const logOnPressLike = useLogOnPressLike();
  const logOnGoodsPressItem = useLogOnGoodsPressItem();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { width: screenWidth } = useScreenSize();

  const itemWidth = useMemo(
    () => (screenWidth - COLUMN_GAP * (NUM_COLUMNS - 1)) / NUM_COLUMNS,
    [screenWidth],
  );

  const goodsEntities = useMemo(() => entity.filter(isGoodsCard), [entity]);

  const videoEntities = useMemo(
    () => entity.filter(
      (e): e is import('../TwoColCardList/types').VideoGoodsCardEntity | import('../TwoColCardList/types').FullVideoGoodsCardEntity =>
        e.type === 'VIDEO_GOODS_CARD' || e.type === 'FULL_VIDEO_GOODS_CARD',
    ),
    [entity],
  );

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...goodsEntities.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
        ...videoEntities.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [goodsEntities, videoEntities, setLikeMap]);

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { addLikeGoods } = useAddLikeGoods({
    listParams: goodsEntities[0]?.logging.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      (goodsEntities[0]?.logging.analytics?.SCREEN as string | undefined) ?? '',
  });

  const listData = useMemo(() => cardRenderItemGenerator(entity), [entity]);

  const handlePressItem = useCallback(
    async ({
      logging,
      item,
    }: {
      logging: IScreensLogging;
      item: GoodsCoreModel;
    }) => {
      logOnGoodsPressItem({ item, logging });

      // 상품 터치 시 기존 inject 캐러셀 제거
      resetInjected?.();

      // entity에서 해당 상품의 on_return 찾기
      if (onReturnAction && componentIndex != null) {
        const goodsEntity = goodsEntities.find(e => e.item.sno === item.sno);
        if (goodsEntity?.item.on_return) {
          const entityIndex = entity.findIndex(
            e => e.type === 'GOODS_CARD' && e.item.sno === item.sno,
          );
          onReturnAction({
            action: goodsEntity.item.on_return,
            sectionIndex: componentIndex,
            itemIndex: entityIndex >= 0 ? entityIndex : 0,
            itemSno: item.sno,
          });
        }
      }

      navigator.moveToGoods({
        sno: item.sno,
        firstPageRendering: item.first_page_rendering ?? null,
        remoteDeeplink: item.remote_deeplink ?? undefined,
        loggingAnalytics: logging.analytics,
        ad: item.ad,
      });
    },
    [logOnGoodsPressItem, navigator, onReturnAction, componentIndex, goodsEntities, entity, resetInjected],
  );

  const handlePressLike = useCallback(async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ logging, item, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  }, [likeMap, logOnPressLike, mutateRemoveLikeGoods, setLikeMap, addLikeGoods]);

  const { logDisplayCardItems } = useLogDisplayCardItems(logDisabled);

  // 비디오 카드 가시성 추적
  const [visibleVideoSnoSet, setVisibleVideoSnoSet] = useState<Set<number>>(new Set());
  const handleViewableItemsChanged = useCallback<
    NonNullable<FlatListProps<CardRenderItem>['onViewableItemsChanged']>
  >(
    (info) => {
      logDisplayCardItems(info);
      const visibleSnoSet = new Set<number>();
      info.viewableItems.forEach(({ item, isViewable }) => {
        if (
          isViewable &&
          (item.type === 'VIDEO_GOODS_CARD' || item.type === 'FULL_VIDEO_GOODS_CARD')
        ) {
          visibleSnoSet.add(item.item.sno);
        }
      });
      setVisibleVideoSnoSet(visibleSnoSet);
    },
    [logDisplayCardItems],
  );

  const handlePressVideoItem = useCallback((item: CardRenderItem) => {
    if (item.type !== 'VIDEO_GOODS_CARD' && item.type !== 'FULL_VIDEO_GOODS_CARD') return;
    logAnalyticsEvent({
      name: 'CLICK_GOODS',
      analytics: { CARD_TYPE: 'VIDEO', ...item.logging.analytics },
      ably_only_log: item.logging.ably_only_log,
    });
    if (item.item.ad) {
      bridge.logAdEvent({ action: 'click' as const, ad: item.item.ad });
    }
    navigator.moveToGoods({
      sno: item.item.sno,
      firstPageRendering: item.item.first_page_rendering ?? null,
      loggingAnalytics: item.logging.analytics,
      ad: item.item.ad,
    });
  }, [navigator, logAnalyticsEvent]);

  const handlePressVideoLike = useCallback((item: CardRenderItem) => {
    if (item.type !== 'VIDEO_GOODS_CARD' && item.type !== 'FULL_VIDEO_GOODS_CARD') return;
    const currentLike = likeMap[item.item.sno] ?? false;
    logAnalyticsEvent({
      name: currentLike ? 'CLICK_UNLIKE_GOODS' : 'CLICK_LIKE_GOODS',
      analytics: { CARD_TYPE: 'VIDEO', ...item.logging.analytics },
      ably_only_log: item.logging.ably_only_log,
    });
    if (item.item.ad) {
      bridge.logAdEvent({
        action: currentLike ? ('unlike' as const) : ('like' as const),
        ad: item.item.ad,
      });
    }
    if (currentLike) {
      mutateRemoveLikeGoods({
        sno: item.item.sno,
        list_params_referrer: item.item.like?.list_id,
      });
      bridge.likeGoods({ goods_sno: item.item.sno, is_liked: false });
      setLikeMap(prev => ({ ...prev, [item.item.sno]: false }));
      return;
    }
    addLikeGoods(item.item.sno, item.logging.analytics, item.logging.ably_only_log);
  }, [likeMap, mutateRemoveLikeGoods, setLikeMap, addLikeGoods, logAnalyticsEvent]);

  const renderItem = ({ item, index }: { item: CardRenderItem; index: number }) => {
    if (item.type === 'PRIMARY_CARD') {
      return (
        <PrimaryCard
          item={item.item}
          logging={item.logging}
          width={itemWidth}
        />
      );
    }
    if (item.type === 'VIDEO_GOODS_CARD') {
      return (
        <VideoGoodsCard
          item={item.item}
          width={itemWidth}
          isLike={likeMap[item.item.sno] ?? false}
          isVisible={visibleVideoSnoSet.has(item.item.sno)}
          onPress={() => handlePressVideoItem(item)}
          onPressLike={() => handlePressVideoLike(item)}
        />
      );
    }
    if (item.type === 'FULL_VIDEO_GOODS_CARD') {
      return (
        <FullVideoGoodsCard
          item={item.item}
          width={itemWidth}
          isLike={likeMap[item.item.sno] ?? false}
          isVisible={visibleVideoSnoSet.has(item.item.sno)}
          onPress={() => handlePressVideoItem(item)}
          onPressLike={() => handlePressVideoLike(item)}
        />
      );
    }
    const feedbackType = item.item?.feedback_type;
    const hasMore = item.render?.data.actions?.includes(AblyGoodsCardActionType.More);
    const handleFeedbackPress =
      onPressMore && hasMore && feedbackType
        ? () => onPressMore(item.sno, feedbackType)
        : undefined;

    return (
      <GoodsCard
        data={item}
        size="medium"
        testID={`goods/${item.sno}?LIST_ID=${item.logging.analytics?.LIST_ID}&LIST_INDEX=${index}&DELIVERY_TYPE=${item.deliveryType}`}
        defaultWidth={itemWidth}
        // ranking={showRanking ? item.item.ranking  : undefined}
        onPress={() => handlePressItem(item)}
        onPressLike={async () => handlePressLike(item)}
        onPressMore={handleFeedbackPress}
        onLongPress={!isWeb ? handleFeedbackPress : undefined}
        isLike={likeMap[item.sno]}
      />
    );
  };

  return (
    <List<CardRenderItem>
      data={listData}
      numColumns={NUM_COLUMNS}
      columnWrapperStyle={{ gap: COLUMN_GAP }}
      onViewableItemsChanged={handleViewableItemsChanged}
      keyExtractor={(item, index) =>
        item.type === 'PRIMARY_CARD'
          ? `primary_${index}`
          : item.type === 'VIDEO_GOODS_CARD' || item.type === 'FULL_VIDEO_GOODS_CARD'
            ? `video_${item.item.sno}_${index}`
            : (item.sno ?? index).toString()
      }
      renderItem={renderItem}
    />
  );
};
